// frontend/src/components/detail/tables/ProductInterviewsTable.jsx
import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, IconButton, Tooltip
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { slugify } from '../../../utils/slug';
import { F } from '../../../utils/qualFmt';

/* ---------- extraction helpers (mirror page helpers) ---------- */

function* iterateProductMentionsInJob(jobNode) {
  if (!jobNode || typeof jobNode !== 'object') return;

  if (jobNode.solution_choice_category) {
    yield {
      solution_choice: jobNode.solution_choice || jobNode.solution || jobNode.solution_choice_category,
      solution_choice_category: jobNode.solution_choice_category,
      satisfaction_level: jobNode.satisfaction_level,
      satisfaction_level_quote: jobNode.satisfaction_level_quote,
      satisfaction_level_justification: jobNode.satisfaction_level_justification,
    };
  }

  const arraysToCheck = [
    'chosen_solutions',
    'solutions_chosen',
    'products_used',
    'solutions',
    'chosen_products',
  ];
  for (const key of arraysToCheck) {
    const arr = jobNode[key];
    if (!Array.isArray(arr)) continue;
    for (const it of arr) {
      if (!it || typeof it !== 'object') continue;
      if (!it.solution_choice_category) continue;
      yield {
        solution_choice: it.solution_choice || it.solution || it.solution_choice_category,
        solution_choice_category: it.solution_choice_category,
        satisfaction_level: it.satisfaction_level,
        satisfaction_level_quote: it.satisfaction_level_quote,
        satisfaction_level_justification: it.satisfaction_level_justification,
      };
    }
  }
}

function collectRows(customerStories = [], productName) {
  const target = slugify(productName || '');
  const rows = [];

  const formatSeg = (seg) => {
    if (!seg || typeof seg !== 'object') return '—';
    const parts = Object.entries(seg).map(([k, v]) => {
      if (v == null) return null;
      if (Array.isArray(v)) return `${k}: ${v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')}`;
      if (typeof v === 'object') {
        const inner = Object.entries(v).map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`).join('; ');
        return `${k}: { ${inner} }`;
      }
      return `${k}: ${String(v)}`;
    }).filter(Boolean);
    return parts.length ? parts.join(' • ') : '—';
  };

  for (const s of customerStories) {
  
    // ✅ 1) Story-level chosen solution
    if (s?.solution_choice_category && slugify(s.solution_choice_category) === target) {
      rows.push({
        interviewName: s.customer_name || 'Interview',
        date: s.conversation_date || '',
        segmentation: formatSeg(s.segmentation_data),
        tags: Array.isArray(s.custom_tags) ? s.custom_tags.join(', ') : (Array.isArray(s.tags) ? s.tags.join(', ') : '—'),
        // object-specific columns:
        solution_choice: s.solution_choice || s.solution || s.solution_choice_category,
        satisfaction_level: s.satisfaction_level ?? '—',
        satisfaction_level_quote: s.satisfaction_level_quote || '—',
        href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
      });
      continue; // one row per interview; skip job scan
    }
  
    const jobs = Array.isArray(s?.jobs_to_be_done) ? s.jobs_to_be_done : [];
    for (const j of jobs) {
      for (const m of iterateProductMentionsInJob(j)) {
        if (slugify(m.solution_choice_category || '') === target) {
          rows.push({
            interviewName: s.customer_name || 'Interview',
            date: s.conversation_date || '',
            segmentation: formatSeg(s.segmentation_data),
            tags: Array.isArray(s.custom_tags) ? s.custom_tags.join(', ') : (Array.isArray(s.tags) ? s.tags.join(', ') : '—'),
            // object-specific columns:
            solution_choice: m.solution_choice || m.solution_choice_category,
            satisfaction_level: m.satisfaction_level || '—',
            satisfaction_level_quote: m.satisfaction_level_quote || '—',
            href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
          });
          break; // one row per interview
        }
      }
    }
  }

  // Sort by date desc if dates exist
  rows.sort((a, b) => {
    const ta = Date.parse(a.date || '') || 0;
    const tb = Date.parse(b.date || '') || 0;
    return tb - ta;
  });

  return rows;
}

/* ---------- component ---------- */

export default function ProductInterviewsTable({ productName, customerStories = [] }) {
  const rows = React.useMemo(
    () => collectRows(customerStories, productName),
    [customerStories, productName]
  );

  if (!rows.length) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No interviews found for this Product.
      </Typography>
    );
  }

  return (
    <TableContainer component={Box} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Interview</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Segmentation</TableCell>
            <TableCell>Custom tags</TableCell>
            {/* object-specific */}
            <TableCell>Solution (raw)</TableCell>
            <TableCell>Primary metric</TableCell>
            <TableCell>Quote</TableCell>
            <TableCell align="center" width={56}>Open</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, idx) => (
            <TableRow key={`prod-int-${idx}`} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {r.interviewName}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {r.date || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {r.segmentation}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {r.tags || '—'}
                </Typography>
              </TableCell>
              {/* object-specific */}
              <TableCell>
                <Typography variant="body2">
                  {r.solution_choice || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {Number.isFinite(Number(r.satisfaction_level))
                    ? F.productSatisfaction(Number(r.satisfaction_level)).text
                    : (r.satisfaction_level || '—')}
                </Typography>
              </TableCell>              
              <TableCell>
                <Typography variant="body2">
                  {r.satisfaction_level_quote || '—'}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Tooltip title="Open interview">
                  <span>
                    <IconButton
                      size="small"
                      component="a"
                      href={r.href}
                      target="_blank"
                      rel="noopener"
                      aria-label="Open interview"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
